import './styles.css'
import 'react-phone-number-input/style.css'
import {useId, useState} from 'react'
import FormErrorMessage from '../FormErrorMessage/FormErrorMessage'
import PhoneInput from 'react-phone-number-input'
import type {CountryCode} from 'libphonenumber-js'
import ClearableFieldWrapper from '../ClearableFieldWrapper/ClearableFieldWrapper'

interface PhoneInputProps {
  phoneInputValue: string
  onChangeCallback: (rawValue: string) => void
  defaultRegion: CountryCode | undefined
  isDisabled: boolean
  onCountryChangeCallback: (country: CountryCode) => void
  flagFormErrors?: boolean
}

const getDigitCounterColor = (length: number) => {
  if (length < 10) {
    return 'text-yellow-600 dark:text-yellow-400'
  }
  return 'text-green-600 dark:text-green-400'
}

export default function PhoneInputComponent({
  phoneInputValue,
  onChangeCallback,
  defaultRegion,
  isDisabled,
  onCountryChangeCallback,
  flagFormErrors,
}: PhoneInputProps) {
  const autoId = useId()
  const inputId = `phone-${autoId}`

  const [focusedStyling, setFocusedStyling] = useState(false)
  const phoneInputValueIsEmpty = phoneInputValue.length === 0

  return (
    <div className="mb-5">
      <div className="flex flex-col gap-1">
        <label
          htmlFor={inputId}
          className={[
            'text-xs text-left mb-[4px]',
            flagFormErrors && phoneInputValueIsEmpty
              ? 'text-red-500 dark:text-red-300'
              : 'text-sky-500 dark:text-sky-400',
          ].join(' ')}
        >
          Contact Phone
          <span className="text-red-600 pl-1">{'*'}</span>
        </label>

        <ClearableFieldWrapper
          showClear={!isDisabled && !phoneInputValueIsEmpty}
          hasError={flagFormErrors && phoneInputValueIsEmpty}
          onClear={() => onChangeCallback('')}
          label="Contact Phone"
          variant="middle-right"
        >
          <PhoneInput
            key={defaultRegion || 'no-country'}
            id={inputId}
            disabled={isDisabled}
            maxLength={16}
            onBlur={() => setFocusedStyling(false)}
            onFocus={() => setFocusedStyling(true)}
            defaultCountry={defaultRegion}
            value={phoneInputValue}
            onChange={(phoneNumber) => onChangeCallback(phoneNumber || '')}
            onCountryChange={(country: CountryCode) => onCountryChangeCallback(country)}
            placeholder="Enter phone number..."
            className={[
              'border px-3 rounded-[4px] text-gray-700 dark:text-slate-200 text-base dark:bg-slate-900/60',
              `${flagFormErrors && phoneInputValueIsEmpty ? 'border-red-300 dark:border-red-500/60' : 'border-gray-300 dark:border-slate-600'}`,
              `${focusedStyling ? 'PhoneInputInput-focused' : ''}`,
              `${focusedStyling && flagFormErrors && phoneInputValueIsEmpty ? ' PhoneInputInput-focused-error' : ''}`,
            ].join(' ')}
          />
        </ClearableFieldWrapper>
      </div>
      {flagFormErrors && phoneInputValueIsEmpty ? (
        <FormErrorMessage message="Contact phone number is required." />
      ) : (
        <div className="text-xs flex text-slate-600 dark:text-slate-400 pt-[5px]">
          <span className="w-[60%] text-left">{phoneInputValue}</span>
          <span
            className={`w-[40%] text-right font-medium ${getDigitCounterColor(phoneInputValue.length)}`}
          >
            {phoneInputValue.length > 0 && `${phoneInputValue.length} digits`}
          </span>
        </div>
      )}
    </div>
  )
}
